"use client";

import { useEffect, useRef } from "react";
// Load-bearing order: the library's own stylesheet must come before our
// brand overrides below. Both files define the same selectors at equal CSS
// specificity, so whichever import runs second wins the cascade — an
// import-sorting lint autofix that reorders these would silently break all
// of the banner's brand styling with no error anywhere.
import "vanilla-cookieconsent/dist/cookieconsent.css";
import "./cookie-consent-banner.css";
import { useLocale } from "@/lib/i18n/client";
import { dictionaries, type Dictionary } from "@/lib/i18n/shared";
import { disableAnalytics, initAnalyticsIfConsented } from "@/lib/analytics";
import { syncAnalyticsConsent } from "@/lib/cookie-consent";

// vanilla-cookieconsent's `language.translations` map is keyed by locale
// code, with each value shaped like this.
function buildTranslation(t: Dictionary["cookieConsent"]) {
  return {
    consentModal: {
      title: t.title,
      description: t.description,
      acceptAllBtn: t.acceptAll,
      acceptNecessaryBtn: t.rejectNonEssential,
      showPreferencesBtn: t.customize,
    },
    preferencesModal: {
      title: t.title,
      acceptAllBtn: t.acceptAll,
      acceptNecessaryBtn: t.rejectNonEssential,
      savePreferencesBtn: t.savePreferences,
      sections: [
        {
          title: t.categories.necessary.name,
          description: t.categories.necessary.description,
          linkedCategory: "necessary",
        },
        {
          title: t.categories.analytics.name,
          description: t.categories.analytics.description,
          linkedCategory: "analytics",
        },
      ],
    },
  };
}

export function CookieConsentBanner() {
  const locale = useLocale();
  // Captured once — this effect only ever runs on mount, so re-renders
  // (e.g. from a locale switch) must not feed it a new "default" language.
  const initialLocaleRef = useRef(locale);
  const hasMountedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    // vanilla-cookieconsent's `run()` guards its entire body behind a
    // module-global `window._ccRun` flag, so calling it again on a later
    // render (e.g. after a locale switch) is a silent no-op — it will not
    // pick up new translations. Both locales' translations are registered
    // up front here instead, and a later effect below uses `setLanguage()`
    // (the library's sanctioned way to change language post-init) to
    // switch between them.
    import("vanilla-cookieconsent").then(({ run }) => {
      if (cancelled) return;

      run({
        guiOptions: {
          consentModal: {
            layout: "box",
            position: "bottom left",
            // Default renders "Accept all" and "Reject" as equally-weighted
            // (both look primary). false gives Reject the secondary/neutral
            // look — it must never look like the destructive/red button
            // used for delete actions elsewhere in this app.
            equalWeightButtons: false,
          },
          preferencesModal: {
            layout: "box",
            // Same reasoning as consentModal above: the preferences modal
            // has its own separate equalWeightButtons key (it does not
            // inherit consentModal's), and defaults to true (both buttons
            // primary/green) if left unset. "Reject" must render neutral
            // here too, not just on the initial banner.
            equalWeightButtons: false,
          },
        },
        // No `cookie.expiresAfterDays` override here — this relies on the
        // library's own default (182 days). lib/cookie-consent.ts's
        // setAnalyticsConsentCookie() deliberately matches that same 182
        // days for our own `analytics_consent` cookie; if either lifetime
        // changes, update the other to match (see the comment there for
        // why they must stay in lockstep).
        categories: {
          necessary: { readOnly: true },
          analytics: {},
        },
        language: {
          default: initialLocaleRef.current,
          translations: {
            pl: buildTranslation(dictionaries.pl.cookieConsent),
            en: buildTranslation(dictionaries.en.cookieConsent),
          },
        },
        onFirstConsent: ({ cookie }) =>
          syncAnalyticsConsent(cookie.categories, initAnalyticsIfConsented, disableAnalytics),
        onChange: ({ cookie }) =>
          syncAnalyticsConsent(cookie.categories, initAnalyticsIfConsented, disableAnalytics),
      });
    }).catch((error) => {
      // Fail closed per docs/superpowers/specs/2026-09-13-cookie-consent-design.md's
      // "Error handling" section: no banner, initAnalyticsIfConsented() stays
      // false (no valid consent cookie), so PostHog never starts. Log only,
      // no user-facing error state.
      console.error("cookie-consent-banner: failed to load vanilla-cookieconsent for run()", error);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  // Locale switching (components/language-switcher.tsx) sets a cookie and
  // calls router.refresh() — no full page reload — so this component stays
  // mounted and must react to the new locale itself. Skip the first run
  // (mount already set the initial language via `language.default` above);
  // on every subsequent change, ask the already-initialized instance to
  // switch language instead of calling `run()` again (a no-op per above).
  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }

    let cancelled = false;

    import("vanilla-cookieconsent")
      .then(({ setLanguage }) => {
        if (cancelled) return;
        setLanguage(locale);
      })
      .catch((error) => {
        // Same fail-closed/log-only handling as the mount effect above —
        // worst case the banner stays in its previously-set language.
        console.error("cookie-consent-banner: failed to load vanilla-cookieconsent for setLanguage()", error);
      });

    return () => {
      cancelled = true;
    };
  }, [locale]);

  return null;
}
